import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { ResetWorkspace } from "@/components/ResetWorkspace";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";

const ROLES = [
  { v: "super_admin", label: "Super Administrator" },
  { v: "secondary_admin", label: "Secondary Administrator" },
  { v: "standard_user", label: "Standard User" },
];
const ROLE_COLOR = { super_admin: "bg-primary text-white", secondary_admin: "border-blue-500 text-blue-600", standard_user: "border-zinc-400 text-zinc-600" };
const empty = { name: "", email: "", password: "", role: "standard_user", active: true };

export default function Users() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [mfaOn, setMfaOn] = useState(true);

  const load = () => api.get("/users").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); api.get("/settings").then(({ data }) => setMfaOn(data.mfa_enabled !== false)).catch(() => {}); }, []);
  const toggleMfa = async (v) => { try { await api.put("/settings", { mfa_enabled: v }); setMfaOn(v); toast.success(v ? "Admin email verification on" : "Admin email verification off"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, active: form.active };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing}`, payload);
      } else {
        await api.post("/users", form);
      }
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (u) => { if (!(await confirm({ title: "Delete user?", description: `"${u.email}" will lose all access to the portal.`, confirmText: "Delete" }))) return; try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div>
      <PageHeader title="Users & Permissions" subtitle="Manage administrators and access levels.">
        <Button onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="add-user-button"><Plus className="h-4 w-4 mr-1.5" /> Add User</Button>
      </PageHeader>
      <label className="mb-4 flex items-center gap-3 text-sm bg-white border border-border rounded-md px-4 py-2.5 w-fit"><Switch checked={mfaOn} onCheckedChange={toggleMfa} data-testid="mfa-enabled-switch" /><span><span className="font-medium">Two-step email verification for administrators</span> <span className="text-muted-foreground">— super and secondary admins receive a 6-digit email code at sign-in (trusted devices skip it for 30 days)</span></span></label>

      <div className="bg-white border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-secondary hover:bg-secondary">
            <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Last Login</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((u) => (
              <TableRow key={u.id} data-testid="user-row">
                <TableCell className="font-medium flex items-center gap-2">{u.role === "super_admin" && <ShieldCheck className="h-4 w-4 text-primary" />}{u.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                <TableCell><Badge variant="outline" className={ROLE_COLOR[u.role]}>{ROLES.find((r) => r.v === u.role)?.label}</Badge></TableCell>
                <TableCell>{u.active ? <Badge variant="outline" className="border-green-600 text-green-700">Active</Badge> : <Badge variant="outline" className="text-muted-foreground">Disabled</Badge>}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{u.last_login ? u.last_login.slice(0, 16).replace("T", " ") : "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setForm({ ...empty, ...u, password: "" }); setEditing(u.id); setOpen(true); }}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => del(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit" : "New"} User</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">Full Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">Email</Label><Input value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">{editing ? "New Password (leave blank to keep)" : "Password"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent></Select></div>
            <div className="flex items-center justify-between"><Label className="text-sm">Account Active</Label><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="user-active-switch" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="save-user-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ResetWorkspace />
    </div>
  );
}
